import {Component, computed, effect, inject, OnDestroy, signal, Signal, viewChild} from '@angular/core';
import {of, Subject} from "rxjs";
import {UserStateService} from "../../users/user-service";
import {watchParam} from "@utils";
import {ArtworkService, EvaluationService, MediaService} from "@api-services";
import {rxResource} from "@angular/core/rxjs-interop";
import {ArtworkDto, CreateArtworkRequestDto, CreateMediaRequestDto, MediaDto} from "@api-contracts";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {switchMap, takeUntil, tap} from "rxjs/operators";
import {FileUploaderComponent} from "../../../../components/forms/file-uploader/file-uploader.component";
import {HttpContext} from "@angular/common/http";
import {SHOW_SPINNER} from "../../../../app.config";
import {TranslocoDirective} from "@jsverse/transloco";
import {MediaRenderComponent} from "../../../../components/media-render/media-render.component";
import {ButtonComponent, InputComponent, InputContainerComponent, ToastService} from "@components";
import {DropdownComponent, DropdownItemDirective} from "../../../../components/layout/dropdown/dropdown.component";

@Component({
  selector: 'wun-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    MediaRenderComponent,
    InputComponent,
    InputContainerComponent,
    DropdownComponent,
    DropdownItemDirective,
    ButtonComponent,
    FileUploaderComponent
  ],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class DetailsComponent implements OnDestroy {
  destroy$ = new Subject<void>();
  public readonly userStateService = inject(UserStateService);
  private readonly artworkService = inject(ArtworkService);
  private readonly evaluationService = inject(EvaluationService);
  private readonly toastService = inject(ToastService);

  private readonly fileUploader = viewChild<FileUploaderComponent>("file");

  id = watchParam('id', Number);
  artwork = rxResource({
    params: this.id,
    stream: params => this.artworkService.artworksGetById({
      id: params.params,
      includes: JSON.stringify(["mainAttachment", "attachments", "evaluation.media"])
    }).pipe(tap(d => {
      const artwork = d.data;
      this.evaluationForm.patchValue({
        shippingCosts: artwork.shippingCosts || 0,
        estimate: artwork.evaluation?.estimate || artwork.price || 0,
      })
      this.form.patchValue(artwork);
    }))
  })
  //
  form = new FormGroup({
    type: new FormControl<CreateArtworkRequestDto.TypeEnum>(CreateArtworkRequestDto.TypeEnum.Physical, Validators.required),
    height: new FormControl<number>(0),
    isBrittle: new FormControl(false),
    description: new FormControl(''),
    depth: new FormControl<number>(0),
    tags: new FormControl(),
    isUnique: new FormControl<boolean>(true),
    name: new FormControl<string>('', Validators.required),
    price: new FormControl<number>(0),
    unit: new FormControl<number>(0),
    weight: new FormControl<number>(0),
    width: new FormControl<number>(0),
    year: new FormControl<number>(0),
    technique: new FormControl<string>(''),
    status: new FormControl<ArtworkDto.StatusEnum>('CREATED'),
  });
  evaluationForm = new FormGroup({
    estimate: new FormControl(0, Validators.required),
    shippingCosts: new FormControl(0, Validators.required),
    media: new FormControl(0),
  })

  thumbs: Signal<MediaDto[]> = computed(() => {
    return this.artwork.value()?.data?.attachments?.filter(d => d.fileScaling === 'THUMBNAIL') || [];
  })

  report = signal<(CreateMediaRequestDto & { file?: File }) | undefined>(undefined)

  constructor() {
    this.userStateService.navbarButtonState.set('IDLE');

    this.userStateService
      .navbarButtonClickedSubject
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.updateArtwork())
      )
      .subscribe(
        d => {
          this.userStateService.navbarButtonState.set('IDLE')
          const _ = this.form.value;
          this.userStateService.currentUser.update(d => {
            return d;
          })
        }
      )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.userStateService.navbarButtonState.set('HIDDEN');
  }

  private updateArtwork() {
    const work = this.artwork.value()!;
    const v = this.form.value;
    return this.artworkService.artworksUpdate({
      id: work.data.id,
      patchArtworkRequestDto: {
        name: v.name!,
        type: v.type!,
        width: Number(v.width),
        height: Number(v.height),
        weight: Number(v.weight),
        depth: Number(v.depth),
        unit: Number(v.unit),
        isUnique: !!v.isUnique,
        description: v.description!,
      }
    });
  }

  fileSelected($event: (CreateMediaRequestDto & { file?: File }) | undefined) {
    this.report.set($event)
  }

  updateWork(evaluationId: number, work: ArtworkDto, status: ArtworkDto.StatusEnum) {
    return this.artworkService.artworksUpdate({
      id: work.id,
      patchArtworkRequestDto: {
        shippingCosts: this.evaluationForm.value.shippingCosts!,
        evaluation: evaluationId,
        width: Number(work.width!),
        height: Number(work.height!),
        depth: Number(work.depth!),
        unit: Number(work.unit!),
        weight: Number(work.weight!),
        price: Number(this.evaluationForm.value.estimate),
        status
      }
    }).pipe(switchMap(f => this.upload(evaluationId)))
  }

  upload(evaluationId: number) {
    if (this.report()) {
      return this.fileUploader()!.upload((d) => {
        return d;
      }, evaluationId)
    }
    return of(null);
  }

  saveEvaluation() {
    const work = this.artwork.value();
    const evaluation = work?.data.evaluation;
    const advise = () => {
      this.toastService.addMessage({
        severity: 'success',
        title: 'Valutazione aggiornata con successo'
      })
    }
    if (evaluation && this.evaluationForm.valid) {

      this.evaluationService.evaluationsUpdate({
        id: evaluation.id,
        patchEvaluationRequestDto: {
          status: 'EVALUATED',
          estimate: this.evaluationForm.value.estimate!,
        }
      }, 'body', false, {
        context: new HttpContext().set(SHOW_SPINNER, true)
      }).pipe(
        switchMap(d =>
          this.updateWork(d.data.id, work?.data!, 'EVALUATED')
        )
      ).subscribe(d => {
        advise()
      })
    } else if (work) {
      this.evaluationService.evaluationsCreate({
        createEvaluationRequestDto: {
          estimate: this.evaluationForm.value.estimate!,
          status: 'EVALUATED',
          description: '-'
        }
      }).pipe(
        switchMap(f => this.updateWork(f.data.id, work?.data!, 'EVALUATED'))
      ).subscribe(d => {
        advise()
      })
    }
  }
}
